/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * What the mapping matches: a directory group or an attribute value.
 */
export const DirectoryRoleMappingSourceKind = {
  Group: "group",
  Attribute: "attribute",
} as const;
/**
 * What the mapping matches: a directory group or an attribute value.
 */
export type DirectoryRoleMappingSourceKind = OpenEnum<
  typeof DirectoryRoleMappingSourceKind
>;

/**
 * Whether the mapped source still exists. missing means the group was deleted from the directory, or no live directory user has the attribute value. A missing source matches nobody, so the mapping grants nothing until the source returns.
 */
export const SourceStatus = {
  Active: "active",
  Missing: "missing",
} as const;
/**
 * Whether the mapped source still exists. missing means the group was deleted from the directory, or no live directory user has the attribute value. A missing source matches nobody, so the mapping grants nothing until the source returns.
 */
export type SourceStatus = OpenEnum<typeof SourceStatus>;

export type DirectoryRoleMapping = {
  /**
   * The directory attribute key. Set when source_kind is attribute.
   */
  attributeKey?: string | undefined;
  /**
   * The directory attribute value. Set when source_kind is attribute.
   */
  attributeValue?: string | undefined;
  createdAt: Date;
  /**
   * The mapped directory group. Set when source_kind is group.
   */
  directoryGroupId?: string | undefined;
  /**
   * Display name of the mapped directory group.
   */
  directoryGroupName?: string | undefined;
  /**
   * Unique mapping identifier.
   */
  id: string;
  /**
   * Principal URN of the role granted to matching members.
   */
  roleUrn: string;
  /**
   * What the mapping matches: a directory group or an attribute value.
   */
  sourceKind: DirectoryRoleMappingSourceKind;
  /**
   * Whether the mapped source still exists. missing means the group was deleted from the directory, or no live directory user has the attribute value. A missing source matches nobody, so the mapping grants nothing until the source returns.
   */
  sourceStatus: SourceStatus;
  updatedAt: Date;
};

/** @internal */
export const DirectoryRoleMappingSourceKind$inboundSchema: z.ZodMiniType<
  DirectoryRoleMappingSourceKind,
  unknown
> = openEnums.inboundSchema(DirectoryRoleMappingSourceKind);

/** @internal */
export const SourceStatus$inboundSchema: z.ZodMiniType<SourceStatus, unknown> =
  openEnums.inboundSchema(SourceStatus);

/** @internal */
export const DirectoryRoleMapping$inboundSchema: z.ZodMiniType<
  DirectoryRoleMapping,
  unknown
> = z.pipe(
  z.object({
    attribute_key: z.optional(z.string()),
    attribute_value: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    directory_group_id: z.optional(z.string()),
    directory_group_name: z.optional(z.string()),
    id: z.string(),
    role_urn: z.string(),
    source_kind: DirectoryRoleMappingSourceKind$inboundSchema,
    source_status: SourceStatus$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "attribute_key": "attributeKey",
      "attribute_value": "attributeValue",
      "created_at": "createdAt",
      "directory_group_id": "directoryGroupId",
      "directory_group_name": "directoryGroupName",
      "role_urn": "roleUrn",
      "source_kind": "sourceKind",
      "source_status": "sourceStatus",
      "updated_at": "updatedAt",
    });
  }),
);

export function directoryRoleMappingFromJSON(
  jsonString: string,
): SafeParseResult<DirectoryRoleMapping, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DirectoryRoleMapping$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DirectoryRoleMapping' from JSON`,
  );
}
